import { useWallet } from '@solana/wallet-adapter-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { normalizeMintingError } from '../utils/errorHandling';
import { captureAndReportError } from '../utils/errorReporting';

interface MintResult {
  mintAddress: string;
  txSignature: string;
}

interface MintFlowOptions {
  isOpen: boolean;
  requiredWalletAddress?: string | null;
}

interface ValidateWalletOptions {
  missingWalletMessage: string;
  disconnectedWalletMessage: string;
  wrongWalletMessage: (walletAddress: string) => string;
}

interface MintErrorContextArgs {
  errorMessage: string;
  retryCount: number;
  nextRetryCount: number;
  walletAddress?: string;
}

interface HandleMintErrorOptions {
  getCaptureContext: (args: MintErrorContextArgs) => Record<string, unknown>;
}

interface HandleMintSuccessOptions {
  mintAddress: string;
  txSignature: string;
  successMessage: string;
  onSuccess: (mintAddress: string, txSignature: string) => void;
  onClose: () => void;
  closeDelayMs?: number;
}

export function useMintFlow({
  isOpen,
  requiredWalletAddress,
}: MintFlowOptions) {
  const wallet = useWallet();
  const { connected, publicKey, signTransaction } = wallet;
  const [isLoading, setIsLoading] = useState(false);
  const [mintResult, setMintResult] = useState<MintResult | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isCorrectWallet, setIsCorrectWallet] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMintResult(null);
      setLastError(null);
      setRetryCount(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (connected && publicKey && requiredWalletAddress) {
      setIsCorrectWallet(publicKey.toBase58() === requiredWalletAddress);
      return;
    }

    setIsCorrectWallet(false);
  }, [connected, publicKey, requiredWalletAddress]);

  const validateWallet = ({
    missingWalletMessage,
    disconnectedWalletMessage,
    wrongWalletMessage,
  }: ValidateWalletOptions) => {
    if (!requiredWalletAddress) {
      toast.error(missingWalletMessage);
      return false;
    }

    if (!connected || !publicKey || !signTransaction) {
      toast.error(disconnectedWalletMessage);
      return false;
    }

    if (!isCorrectWallet) {
      toast.error(wrongWalletMessage(requiredWalletAddress));
      return false;
    }

    return true;
  };

  const handleMintError = (
    error: unknown,
    { getCaptureContext }: HandleMintErrorOptions,
  ) => {
    const normalizedError = normalizeMintingError(error);
    const errorMessage = normalizedError.message;
    const walletAddress = publicKey?.toBase58();
    const nextRetryCount = retryCount + 1;

    if (!normalizedError.isUserRejection) {
      captureAndReportError(
        error,
        getCaptureContext({
          errorMessage,
          retryCount,
          nextRetryCount,
          walletAddress,
        }),
      );
    }

    setLastError(errorMessage);
    setRetryCount((previous) => previous + 1);
    toast.error(errorMessage);
  };

  const handleMintSuccess = ({
    mintAddress,
    txSignature,
    successMessage,
    onSuccess,
    onClose,
    closeDelayMs = 3000,
  }: HandleMintSuccessOptions) => {
    setMintResult({ mintAddress, txSignature });
    toast.success(successMessage);
    onSuccess(mintAddress, txSignature);
    setTimeout(() => {
      onClose();
    }, closeDelayMs);
  };

  return {
    wallet,
    connected,
    publicKey,
    signTransaction,
    isLoading,
    setIsLoading,
    mintResult,
    lastError,
    retryCount,
    isCorrectWallet,
    validateWallet,
    handleMintError,
    handleMintSuccess,
  };
}
